import { Button, Grid, TextField, Typography, useTheme, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { TOKEN_KEY, url, imgUrl } from "src/Helpers/utils/Constant";
import Toaster from "src/Toaster/Toaster";

interface IProps {
  heroimage: any;
  handleClose: Function;
  setRefreshTrigger: Function;
}

export default function UpdateHeroImage({ heroimage, handleClose, setRefreshTrigger }: IProps) {
  const { register, handleSubmit, reset } = useForm<any>({
    defaultValues: {
      order: heroimage?.order ?? "",
      status: heroimage?.status ?? "active",
      alt_text: heroimage?.alt_text ?? "",
    },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (heroimage) {
      reset({
        order: heroimage.order ?? "",
        status: heroimage.status ?? "active",
        alt_text: heroimage.alt_text ?? "",
      });
    }
  }, [heroimage, reset]);

  const onSubmit = async (values: any) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("order", values.order);
      formData.append("status", values.status);
      formData.append("alt_text", values.alt_text || "");
      if (values.image && values.image[0]) {
        formData.append("image", values.image[0]);
      }

      const { data } = await axios.patch(
        `${url}/api/v1/admin/website-slider/${heroimage.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: "success",
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        setLoading(false);
        handleClose();
      } else {
        Toast.fire({
          icon: "error",
          title: `${data.message}`,
        });
        setLoading(false);
      }
    } catch (err: any) {
      Toast.fire({
        icon: "error",
        title: `${err.response?.data?.message || "Something went wrong"}`,
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        sx={{ p: 2, mt: 1 }}
      >
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <TextField
              margin="normal"
              fullWidth
              required
              id="order"
              label="Type an order"
              {...register("order")}
              type="number"
              autoFocus
              inputProps={{ step: 1, min: 1 }}
              variant="outlined"
            />
          </Grid>

          <Grid xs={12} sm={6} item>
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                defaultValue={heroimage?.status ?? "active"}
                {...register("status")}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={12} item>
            <TextField
              margin="normal"
              fullWidth
              id="alt_text"
              label="Alt text"
              {...register("alt_text")}
              type="text"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            {heroimage?.url && (
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Current Image:
                </Typography>
                <img
                  src={`${imgUrl}/${heroimage.url}`}
                  alt="Current Hero Slider"
                  style={{
                    maxWidth: "200px",
                    maxHeight: "150px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            )}
            <TextField
              margin="normal"
              fullWidth
              id="image"
              label="Upload new image (optional)"
              inputProps={{
                accept: "image/png, image/jpeg, image/jpg, image/webp",
              }}
              InputLabelProps={{ shrink: true }}
              autoComplete="image"
              type="file"
              {...register("image")}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Button
            disabled
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            loading...
          </Button>
        ) : (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              mt: 3,
              mb: 2,
              py: 1,
            }}
          >
            Update Hero Image
          </Button>
        )}
      </Box>
    </>
  );
}
